import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirebase } from "../../../../context/FirebaseContext";
import { getDoc, doc } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "../../../../firebase/firebase";
import { getStoreSettings } from "../../../../firebase/settings-functions";
import { getLocalizedText } from "../../../../utils/multilingualUtils";
import "./OrderInvoice.css";

const OrderInvoice = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();

  const [order, setOrder] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchOrderAndSettings = async () => {
      setLoading(true);
      setError("");

      try {
        // Fetch store settings
        const settings = await getStoreSettings(DEFAULT_STORE_ID);
        setStoreSettings(settings);

        // Get order document from Firestore
        const orderRef = doc(
          db,
          "stores",
          DEFAULT_STORE_ID,
          "orders",
          orderId
        );
        const orderDoc = await getDoc(orderRef);

        if (!orderDoc.exists()) {
          setError("Order not found");
          setLoading(false);
          return;
        }

        // Set order data
        setOrder({
          id: orderDoc.id,
          ...orderDoc.data(),
        });
      } catch (err) {
        console.error("Error fetching invoice data:", err);
        setError("Failed to load invoice data");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderAndSettings();
  }, [orderId]);

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    // Handle Firestore Timestamp objects
    let date;
    if (timestamp && typeof timestamp.toDate === 'function') {
      // It's a Firestore Timestamp
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      // It's a string, parse it
      date = new Date(timestamp);
    } else {
      // Fallback
      return "";
    }

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format currency values safely
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "0.00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  // Generate invoice number  
  const generateInvoiceNumber = (order) => {
    // Use order number if available for simple #INV-1, #INV-2 format
    if (order.orderNumber) {
      return `INV-${order.orderNumber}`;
    }

    // Fallback for legacy orders without orderNumber  
    const orderPrefix = order.id.substring(0, 8).toUpperCase();
    const timestamp = new Date().getTime().toString().substring(7);
    return `INV-${orderPrefix}-${timestamp}`;
  };

  // Handle print invoice
  const handlePrint = () => {
    // Use the browser's built-in print functionality
    window.print();
  };

  // Handle download PDF
  const handleDownloadPDF = () => {
    // Create a temporary link to trigger download
    const printContent = printRef.current.innerHTML;

    // Create a new window with the invoice content
    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice #${order?.id}</title>
          <meta charset="utf-8">
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            .text-right {
              text-align: right;
            }
            .total-row {
              font-weight: bold;
              background-color: #f8f9fa;
            }
            .invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #333;
            }
            .invoice-logo img {
              max-height: 60px;
              max-width: 200px;
            }
            .invoice-title {
              text-align: right;
            }
            .invoice-title h1 {
              margin: 0;
              font-size: 2.1em;
              color: #333;
            }
            .invoice-number {
              margin: 5px 0;
              font-size: 0.5em;
              color: #666;
            }
            .invoice-info {
              margin-bottom: 30px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 5px 0;
              border-bottom: 1px solid #eee;
            }
            .info-label {
              font-weight: bold;
              color: #555;
            }
            .address-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 30px;
            }
            .address-block {
              flex: 1;
              margin-right: 30px;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 5px;
            }
            .address-block:last-child {
              margin-right: 0;
            }
            .address-block h3 {
              margin-top: 0;
              color: #333;
              border-bottom: 1px solid #ddd;
              padding-bottom: 10px;
            }
            .address p {
              margin: 5px 0;
              line-height: 1.4;
            }
            .name {
              font-weight: bold;
              font-size: 0.5em;
            }
            .invoice-footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #333;
              display: flex;
              justify-content: space-between;
              font-size: 0.9em;
            }
            .invoice-footer > div {
              flex: 1;
              margin-right: 20px;
            }
            .invoice-footer > div:last-child {
              margin-right: 0;
            }
            .thank-you {
              text-align: center;
              font-size: 0.8em;
              font-weight: bold;
              color: #333;
            }
            .download-buttons {
              text-align: center;
              margin: 20px 0;
            }
            .download-buttons button {
              margin: 0 10px;
              padding: 10px 20px;
              background: #007bff;
              color: white;
              border: none;
              border-radius: 5px;
              cursor: pointer;
            }
            .download-buttons button:hover {
              background: #0056b3;
            }
            @media print {
              .download-buttons { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            ${printContent}
          </div>
          <div class="download-buttons no-print">
            <button onclick="window.print()">Print Invoice</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
        </html>
      `);

      printWindow.document.close();

      // Focus the new window
      printWindow.focus();
    } else {
      // Fallback if popup is blocked
      alert('Please allow popups for this site to download the invoice, or use the Print button instead.');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loader"></div>
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button
          className="back-button"
          onClick={() => navigate(`/admin/orders/${orderId}`)}
        >
          Back to Order
        </button>
      </div>
    );
  }

  if (!order || !storeSettings) {
    return (
      <div className="admin-error-container">
        <h2>Invoice Data Not Found</h2>
        <p>The requested invoice data could not be loaded.</p>
        <button
          className="back-button"
          onClick={() => navigate(`/admin/orders/${orderId}`)}
        >
          Back to Order
        </button>
      </div>
    );
  }

  const invoiceNumber = generateInvoiceNumber(order);
  const orderDate = formatDate(order.createdAt);
  const invoiceDate = formatDate(new Date().toISOString());

  return (
    <>
      <style>
        {`
          @media print {
            .invoice-actions {
              display: none !important;
            }
            .invoice-page {
              margin: 0 !important;
              padding: 0 !important;
            }
            body {
              margin: 0 !important;
              padding: 10px !important;
            }
          }
        `}
      </style>
      <div className="invoice-page">
        <div className="invoice-actions no-print">
          <button
            className="back-button"
            onClick={() => navigate(`/admin/orders/${orderId}`)}
          >
            Back to Order
          </button>
          <button className="print-button" onClick={handlePrint}>
            Print Invoice
          </button>
          <button className="download-button" onClick={handleDownloadPDF}>
            Download PDF
          </button>
        </div>

        <div className="invoice-container" ref={printRef}>
          <div className="invoice-header">
            <div className="invoice-logo">
              {storeSettings.logoUrl ? (
                <img src={storeSettings.logoUrl} alt={storeSettings.name} />
              ) : (
                <h1>
                  {storeSettings.name || "Your Store"}
                </h1>
              )}
            </div>
            <div className="invoice-title">
              <h1>INVOICE</h1>
              <p className="invoice-number">#{invoiceNumber}</p>
            </div>
          </div>

          <div className="invoice-info">
            <div className="invoice-dates">
              <div className="info-row">
                <span className="info-label">Invoice Date:</span>
                <span className="info-value">{invoiceDate}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Order Date:</span>
                <span className="info-value">{orderDate}</span>
              </div>
              {order.orderNumber ? (
                <>
                  <div className="info-row">
                    <span className="info-label">Order Number:</span>
                    <span className="info-value">#{order.orderNumber}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Order ID:</span>
                    <span className="info-value">{order.id}</span>
                  </div>
                </>
              ) : (
                <div className="info-row">
                  <span className="info-label">Order ID:</span>
                  <span className="info-value">#{order.id}</span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Payment Method:</span>
                <span className="info-value">
                  {order.paymentMethod
                    ?.replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="address-section">
            <div className="address-block">
              <h3>Bill To:</h3>
              {order.billingInfo && (
                <div className="address">
                  <p className="name">
                    {order.billingInfo.firstName} {order.billingInfo.lastName}
                  </p>
                  <p>{order.billingInfo.address}</p>
                  <p>
                    {order.billingInfo.city}, {order.billingInfo.state}{" "}
                    {order.billingInfo.zipCode}
                  </p>
                  <p>{order.billingInfo.country}</p>
                  <p>{order.billingInfo.email}</p>
                  <p>{order.billingInfo.phone}</p>
                </div>
              )}
            </div>

            <div className="address-block">
              <h3>Ship To:</h3>
              {order.shippingInfo && (
                <div className="address">
                  <p className="name">
                    {order.shippingInfo.firstName} {order.shippingInfo.lastName}
                  </p>
                  <p>{order.shippingInfo.address}</p>
                  <p>
                    {order.shippingInfo.city}, {order.shippingInfo.state}{" "}
                    {order.shippingInfo.zipCode}
                  </p>
                  <p>{order.shippingInfo.country}</p>
                </div>
              )}
            </div>
          </div>

          <div className="invoice-items">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items &&
                  order.items.map((item, index) => (
                    <tr key={index}>
                      <td>{getLocalizedText(item.name, 'en')}</td>
                      <td>
                        {item.displayVariant ? (
                          <div className="variant-info">
                            <span>{item.displayVariant}</span>
                          </div>
                        ) : (
                          "Standard"
                        )}
                      </td>
                      <td>
                        {order.currency} {formatCurrency(item.price)}
                      </td>
                      <td>{item.quantity}</td>
                      <td>
                        {order.currency}{" "}
                        {formatCurrency(item.price * item.quantity)}
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="text-right">
                    Subtotal
                  </td>
                  <td>
                    {order.currency} {formatCurrency(order.subtotal)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" className="text-right">
                    Shipping ({order.shipping?.method})
                  </td>
                  <td>
                    {order.currency}{" "}
                    {formatCurrency(order.shipping?.cost) || "0.00"}
                  </td>
                </tr>
                {((order.discount && order.discount > 0) || (order.discountAmount && order.discountAmount > 0) || (order.promoCode?.discountAmount && order.promoCode.discountAmount > 0)) && (
                  <tr>
                    <td colSpan="4" className="text-right">
                      Discount{order.promoCode?.code && ` (${order.promoCode.code})`}
                    </td>
                    <td style={{ color: '#28a745', fontWeight: 'bold' }}>
                      -{order.currency} {formatCurrency(order.discount || order.discountAmount || order.promoCode?.discountAmount || 0)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan="4" className="text-right">
                    Tax
                  </td>
                  <td>
                    {order.currency} {formatCurrency(order.tax?.amount) || "0.00"}
                  </td>
                </tr>
                <tr className="total-row">
                  <td colSpan="4" className="text-right">
                    Total
                  </td>
                  <td>
                    {order.currency} {formatCurrency(order.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="invoice-footer">
            <div className="payment-info">
              <h3>Payment Status: {order.paymentStatus || "Pending"}</h3>

            </div>

            <div className="store-info">

              <p>Email: {storeSettings.email || "N/A"}</p>
              <p>Phone: {storeSettings.phone || "N/A"}</p>

            </div>

            <div className="thank-you">
              <p>Thank you for your business!</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderInvoice;